import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { interactionAPI, customerAPI } from "../lib/api";
import { formatDateTime } from "../lib/utils";
import { Plus, MessageSquare, Clock, User, Trash2, Edit2, History, Search } from "lucide-react";
import { toast } from "sonner";

const INTERACTION_TYPES = [
  { value: "phone_call", label: "Phone Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
  { value: "support", label: "Support" },
  { value: "sales", label: "Sales" }
];

export function Interactions() {
  const { canEditInteractions, isAdmin } = useAuth();
  const [interactions, setInteractions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [historyInteraction, setHistoryInteraction] = useState(null);
  const [sortBy, setSortBy] = useState("date_desc"); // Default sort by newest date
  const [searchTerm, setSearchTerm] = useState(""); // Search by customer name
  const [newInteraction, setNewInteraction] = useState({
    customer_id: "",
    type: "", // Blank default, compulsory field
    date: "", // Will be set to current datetime when dialog opens
    duration_minutes: "",
    pending_job: false, // New field
    notes: ""
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load customers and interactions separately to handle errors independently
      try {
        const customersRes = await customerAPI.getAll();
        console.log("Customers loaded:", customersRes.data);
        setCustomers(customersRes.data);
      } catch (customerError) {
        console.error("Failed to load customers:", customerError);
        toast.error("Failed to load customers");
      }

      try {
        const interactionsRes = await interactionAPI.getAll();
        setInteractions(interactionsRes.data);
      } catch (interactionError) {
        console.error("Failed to load interactions:", interactionError);
        toast.error("Failed to load interactions");
      }

    } catch (error) {
      console.error("General load data error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateInteraction = async (e) => {
    e.preventDefault();
    if (!newInteraction.customer_id || !newInteraction.type || !newInteraction.duration_minutes) {
      toast.error("Customer, interaction type, and duration are required");
      return;
    }

    try {
      const interactionData = {
        ...newInteraction,
        duration_minutes: parseInt(newInteraction.duration_minutes),
        // datetime-local input provides both date and time, convert to ISO string
        date: newInteraction.date ? new Date(newInteraction.date).toISOString() : null
      };
      
      await interactionAPI.create(interactionData);
      toast.success("Interaction logged successfully");
      setShowCreateDialog(false);
      setNewInteraction({
        customer_id: "",
        type: "", // Blank default, compulsory field
        date: "",
        duration_minutes: "",
        pending_job: false,
        notes: ""
      });
      loadData();
    } catch (error) {
      toast.error("Failed to create interaction");
      console.error("Create interaction error:", error);
    }
  };

  const handleEditInteraction = (interaction) => {
    const editableInteraction = {
      id: interaction.id,
      customer_id: interaction.customer_id,
      type: interaction.type,
      date: interaction.date ? new Date(interaction.date).toISOString().slice(0, 16) : "",
      duration_minutes: interaction.duration_minutes || "",
      pending_job: interaction.pending_job || false,
      notes: interaction.notes || ""
    };
    setEditingInteraction(editableInteraction);
    setShowEditDialog(true);
  };

  const handleUpdateInteraction = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        ...editingInteraction,
        duration_minutes: parseInt(editingInteraction.duration_minutes),
        date: editingInteraction.date ? new Date(editingInteraction.date).toISOString() : null
      };
      
      // Remove id from update data
      const { id, ...updateFields } = updateData;
      
      await interactionAPI.update(id, updateFields);
      toast.success("Interaction updated successfully");
      setShowEditDialog(false);
      setEditingInteraction(null);
      loadData();
    } catch (error) {
      toast.error("Failed to update interaction");
      console.error("Update error:", error);
    }
  };

  const handleShowHistory = (interaction) => {
    setHistoryInteraction(interaction);
    setShowHistoryDialog(true);
  };

  const handleDeleteInteraction = async (interactionId) => {
    if (!window.confirm("Are you sure you want to delete this interaction? This action cannot be undone.")) {
      return;
    }

    try {
      await interactionAPI.delete(interactionId);
      toast.success("Interaction deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete interaction");
      console.error("Delete error:", error);
    }
  };

  const getInteractionTypeColor = (type) => {
    const colors = {
      phone_call: "bg-blue-100 text-blue-800",
      email: "bg-green-100 text-green-800",
      meeting: "bg-purple-100 text-purple-800",
      support: "bg-orange-100 text-orange-800",
      sales: "bg-cyan-100 text-cyan-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : "Unknown Customer";
  };

  const getFilteredAndSortedInteractions = () => {
    // First filter by search term
    let filtered = interactions;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = interactions.filter(interaction => {
        const customerName = getCustomerName(interaction.customer_id).toLowerCase();
        return customerName.includes(search);
      });
    }
    
    // Then sort
    const sorted = [...filtered];
    switch (sortBy) {
      case "date_desc":
        return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      case "date_asc":
        return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
      case "customer_asc":
        return sorted.sort((a, b) => getCustomerName(a.customer_id).localeCompare(getCustomerName(b.customer_id)));
      case "customer_desc":
        return sorted.sort((a, b) => getCustomerName(b.customer_id).localeCompare(getCustomerName(a.customer_id)));
      default:
        return sorted;
    }
  };

  const getSortedInteractions = () => {
    return getFilteredAndSortedInteractions();
  };

  const handleDialogOpenChange = (open) => {
    setShowCreateDialog(open);
    if (open) {
      // Set current datetime when dialog opens
      const now = new Date();
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setNewInteraction(prev => ({
        ...prev,
        date: localDateTime
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="interactions-title">
            Interactions
          </h1>
          <p className="text-slate-600 mt-1">Track customer interactions and communications</p>
        </div>
        
        {canEditInteractions && (
          <Dialog open={showCreateDialog} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-interaction-button">
                <Plus className="w-4 h-4 mr-2" />
                Log Interaction
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Log New Interaction</DialogTitle>
              <DialogDescription>
                Record a customer interaction or communication.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateInteraction} className="space-y-4" data-testid="create-interaction-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer">Customer *</Label>
                  <Select
                    value={newInteraction.customer_id}
                    onValueChange={(value) => setNewInteraction({ ...newInteraction, customer_id: value })}
                  >
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers && customers.length > 0 ? (
                        customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="" disabled>
                          No customers available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="type">Interaction Type *</Label>
                  <Select
                    value={newInteraction.type}
                    onValueChange={(value) => setNewInteraction({ ...newInteraction, type: value })}
                  >
                    <SelectTrigger data-testid="type-select">
                      <SelectValue placeholder="Select type *" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERACTION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date">Interaction Date & Time</Label>
                  <Input
                    id="date"
                    type="datetime-local"
                    value={newInteraction.date}
                    onChange={(e) => setNewInteraction({ ...newInteraction, date: e.target.value })}
                    data-testid="interaction-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="duration">Duration (minutes) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={newInteraction.duration_minutes}
                    onChange={(e) => setNewInteraction({ ...newInteraction, duration_minutes: e.target.value })}
                    placeholder="e.g. 30"
                    required
                    data-testid="interaction-duration-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newInteraction.notes}
                  onChange={(e) => setNewInteraction({ ...newInteraction, notes: e.target.value })}
                  placeholder="What was discussed or accomplished?"
                  rows={4}
                  data-testid="interaction-notes-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="pending_job"
                  checked={newInteraction.pending_job}
                  onChange={(e) => setNewInteraction({ ...newInteraction, pending_job: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  data-testid="pending-job-checkbox"
                />
                <Label htmlFor="pending_job">Pending Job</Label>
              </div>
              
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1" data-testid="save-interaction-button">
                  Log Interaction
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  data-testid="cancel-interaction-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Search and Sorting Controls */}
      {interactions.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search Box */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="interactions-search"
              />
            </div>
            {/* Sort Dropdown */}
            <div className="flex items-center space-x-2">
              <Label htmlFor="sort-by">Sort by:</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48" data-testid="interactions-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Date (Newest First)</SelectItem>
                  <SelectItem value="date_asc">Date (Oldest First)</SelectItem>
                  <SelectItem value="customer_asc">Customer (A-Z)</SelectItem>
                  <SelectItem value="customer_desc">Customer (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm text-slate-600">
            {searchTerm ? `${getSortedInteractions().length} of ${interactions.length}` : interactions.length} interaction{interactions.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Interactions List */}
      {interactions.length > 0 ? (
        <div className="space-y-4" data-testid="interactions-list">
          {getSortedInteractions().map((interaction) => (
            <Card key={interaction.id} className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">
                          {getCustomerName(interaction.customer_id)}
                        </span>
                      </div>
                      <Badge className={getInteractionTypeColor(interaction.type)}>
                        {interaction.type.replace('_', ' ')}
                      </Badge>
                      {interaction.duration_minutes && (
                        <div className="flex items-center text-sm text-slate-600">
                          <Clock className="w-4 h-4 mr-1" />
                          {interaction.duration_minutes} min
                        </div>
                      )}
                    </div>
                    
                    {interaction.notes && (
                      <p className="text-slate-700 mb-3 leading-relaxed">{interaction.notes}</p>
                    )}
                    
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500">
                        {formatDateTime(interaction.date)}
                      </p>
                      
                      {/* Creator Information */}
                      {interaction.created_by && (
                        <p className="text-xs text-slate-400">
                          Created by {interaction.created_by}
                          {interaction.created_date && ` on ${formatDateTime(interaction.created_date)}`}
                        </p>
                      )}
                      
                      {/* Last Edit Information */}
                      {interaction.last_edited_by && (
                        <p className="text-xs text-slate-400">
                          Last edited by {interaction.last_edited_by}
                          {interaction.last_edited_date && ` on ${formatDateTime(interaction.last_edited_date)}`}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <MessageSquare className="w-5 h-5 text-slate-400" />
                    
                    {/* Edit Button - All users can edit */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditInteraction(interaction)}
                      className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      title="Edit interaction"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    
                    {/* History Button - Show if there's edit history */}
                    {interaction.edit_history && interaction.edit_history.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShowHistory(interaction)}
                        className="text-green-600 hover:text-green-800 hover:bg-green-50"
                        title="View edit history"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                    )}
                    
                    {/* Delete Button - Admin only */}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteInteraction(interaction.id)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        title="Delete interaction (Admin only)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-16">
            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2" data-testid="no-interactions-title">
              No interactions logged
            </h3>
            <p className="text-slate-600 mb-6">
              Start tracking customer communications and interactions
            </p>
            {canEditInteractions && (
              <Button onClick={() => handleDialogOpenChange(true)} data-testid="add-first-interaction-button">
                <Plus className="w-4 h-4 mr-2" />
                Log Your First Interaction
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Interaction Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Interaction</DialogTitle>
            <DialogDescription>
              Update the interaction details. Changes will be tracked in the edit history.
            </DialogDescription>
          </DialogHeader>
          
          {editingInteraction && (
            <form onSubmit={handleUpdateInteraction} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-customer">Customer *</Label>
                  <Select
                    value={editingInteraction.customer_id}
                    onValueChange={(value) => setEditingInteraction({ ...editingInteraction, customer_id: value })}
                  >
                    <SelectTrigger id="edit-customer">
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-type">Interaction Type *</Label>
                  <Select
                    value={editingInteraction.type}
                    onValueChange={(value) => setEditingInteraction({ ...editingInteraction, type: value })}
                  >
                    <SelectTrigger id="edit-type">
                      <SelectValue placeholder="Select interaction type" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERACTION_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-date">Interaction Date & Time</Label>
                  <Input
                    id="edit-date"
                    type="datetime-local"
                    value={editingInteraction.date}
                    onChange={(e) => setEditingInteraction({ ...editingInteraction, date: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="edit-duration">Duration (minutes) *</Label>
                  <Input
                    id="edit-duration"
                    type="number"
                    min="1"
                    value={editingInteraction.duration_minutes}
                    onChange={(e) => setEditingInteraction({ ...editingInteraction, duration_minutes: e.target.value })}
                    placeholder="e.g. 30"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editingInteraction.notes}
                  onChange={(e) => setEditingInteraction({ ...editingInteraction, notes: e.target.value })}
                  placeholder="Add any additional notes or details..."
                  className="min-h-[100px]"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-pending"
                  checked={editingInteraction.pending_job}
                  onChange={(e) => setEditingInteraction({ ...editingInteraction, pending_job: e.target.checked })}
                />
                <Label htmlFor="edit-pending">Mark as Pending Job</Label>
              </div>

              <div className="flex justify-end space-x-3">
                <Button type="submit">Update Interaction</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit History</DialogTitle>
            <DialogDescription>
              Complete audit trail of all changes made to this interaction.
            </DialogDescription>
          </DialogHeader>
          
          {historyInteraction && (
            <div className="space-y-4">
              {/* Original Creation */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-900 mb-2">Original Creation</h4>
                <p className="text-sm text-green-700">
                  Created by <strong>{historyInteraction.created_by || 'Unknown'}</strong>
                  {historyInteraction.created_date && (
                    <span> on {formatDateTime(historyInteraction.created_date)}</span>
                  )}
                </p>
              </div>

              {/* Edit History */}
              {historyInteraction.edit_history && historyInteraction.edit_history.length > 0 ? (
                <div className="space-y-3">
                  {historyInteraction.edit_history.map((entry, index) => (
                    <div key={index} className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-medium text-blue-900">
                          Edit #{historyInteraction.edit_history.length - index}
                        </h4>
                        <span className="text-xs text-blue-600">
                          {formatDateTime(entry.edited_date)}
                        </span>
                      </div>
                      
                      <p className="text-sm text-blue-700 mb-3">
                        Edited by <strong>{entry.edited_by}</strong>
                      </p>
                      
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-blue-900">Changes made:</h5>
                        {Object.entries(entry.changes).map(([field, change]) => (
                          <div key={field} className="text-sm">
                            <strong className="text-blue-800 capitalize">
                              {field.replace('_', ' ')}:
                            </strong>
                            <div className="ml-4 grid grid-cols-1 gap-1">
                              <div className="text-red-600">
                                - Old: {change.old || 'None'}
                              </div>
                              <div className="text-green-600">
                                + New: {change.new}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No edit history available</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
